import {
  ALIGN_FACTOR,
  BASE_SPEED,
  CORNER_SLIP_FACTOR,
  BLAST_TICKS,
  BOMB_FUSE_TICKS,
  CHAIN_DELAY_TICKS,
  CURSE_POOL,
  CURSE_TICKS,
  Curse,
  DIR_VECTORS,
  ENEMY_HURT_TICKS,
  ENEMY_SPAWN_CLEARANCE,
  ENEMY_TOUCH_RANGE,
  CATCHUP_BIAS,
  CHARGE_CHECK_SECONDS,
  CHARGE_DEFICIT,
  CATCHUP_RANGE,
  DROP_TABLE,
  materialPower,
  DT,
  STALKER_SIGHT,
  Dir,
  FULL_FIRE_POWER,
  GHOST_TICKS,
  Item,
  KICK_SPEED,
  MAX_BOMBS,
  MAX_POWER,
  MAX_SPEED_LEVEL,
  PLAYER_RADIUS,
  PUNCH_DISTANCE,
  PUNCH_TICKS,
  RESPAWN_TICKS,
  SHIELD_TICKS,
  SHORT_FUSE_TICKS,
  FARM_INVULN_MAX_DEATHS,
  HURT_INVULN_TICKS,
  BLAST_FALLOFF_MIN,
  MAX_HEALTH,
  RESOLVE_LETHAL_MAX,
  RESOLVE_RANGE,
  SUPPLY_TABLE,
  SUPPLY_CLEARANCE,
  SUPPLY_WARN_SECONDS,
  SUPPLY_COUNT,
  SUPPLY_AT,
  RESOLVE_RESIST_MAX,
  FARM_INVULN_PER_DEATH,
  FARM_SCORE_DECAY,
  FARM_SCORE_FLOOR,
  SPAWN_INVULN_TICKS,
  SPEED_STEP,
  START_BOMBS,
  START_POWER,
  SUDDEN_DEATH_INTERVAL,
  SUDDEN_DEATH_WARN_TICKS,
  TICK_HZ,
  Tile,
} from './constants';
import type { CurseValue, DirValue, ItemValue } from './constants';
import { buildMap, getArena, spiralOrder } from './maps';
import { Rng } from './rng';
import { validCheckpoint, type SimCheckpoint } from './checkpoint';
import {
  FAN_BOMB,
  FAN_PUSH,
  Hazard,
  ITEM_DRIFT_TICKS,
  LAUNCH_SPEED,
  LAUNCH_TICKS,
  SLIDE_FACTOR,
  SLIDE_TICKS,
  fanVector,
  hazardHarms,
  hazardLive,
  hazardSpeed,
} from './hazards';
import type {
  BlastState,
  BombState,
  EnemyKindValue,
  EnemySpawn,
  EnemyState,
  GameEvent,
  GameState,
  Input,
  MatchSettings,
  PlayerState,
  WinCondition,
} from './types';
import { EMPTY_INPUT, EnemyKind, RoomGoal } from './types';

/**
 * What a player walks into a room already holding.
 *
 * A match normally starts everyone equal, which is right when the match is the
 * whole game. It is wrong for a mode that chains rooms into a run: losing the
 * range you earned two rooms ago every time a door opens is the difference
 * between a run and nine unrelated matches.
 *
 * Optional, and absent everywhere but the run mode, so every existing caller
 * gets the same fresh player it always did. Applied through the same caps the
 * pickups respect, because a seed arrives over the wire in online play and
 * `maxBombs: 400` should be a clamp rather than a trust exercise.
 */
export interface PlayerGear {
  maxBombs?: number;
  power?: number;
  speedLevel?: number;
  kick?: boolean;
  punch?: boolean;
  remote?: boolean;
  pierce?: boolean;
}

export interface PlayerSeed {
  id: string;
  name: string;
  slot: number;
  team?: number;
  bot?: boolean;
  botLevel?: number;
  /** Carried in from an earlier room. See `PlayerGear`. */
  gear?: PlayerGear;
}

const EPS = 1e-6;

/* -------------------------------------------------------------------------- */
/* Grid helpers                                                                */
/* -------------------------------------------------------------------------- */

export function idxOf(state: GameState, cx: number, cy: number): number {
  return cy * state.width + cx;
}

export function inBounds(state: GameState, cx: number, cy: number): boolean {
  return cx >= 0 && cy >= 0 && cx < state.width && cy < state.height;
}

export function tileAt(state: GameState, cx: number, cy: number): number {
  if (!inBounds(state, cx, cy)) return Tile.HARD;
  return state.grid[idxOf(state, cx, cy)];
}

export function bombAt(state: GameState, cx: number, cy: number): BombState | null {
  for (const b of state.bombs) {
    if (b.flight > 0) continue;
    if (Math.floor(b.x) === cx && Math.floor(b.y) === cy) return b;
  }
  return null;
}

/**
 * A bomb passing over a tile, mid-punch.
 *
 * Separate from `bombAt` on purpose: that one skips bombs in flight because
 * everything asking it -- movement, kicks, whether a tile can be entered -- is
 * asking about something solid, and a bomb in the air is not. This is the one
 * question where the answer is the opposite, so it is a different question.
 */
export function flyingBombAt(state: GameState, cx: number, cy: number): BombState | null {
  for (const b of state.bombs) {
    if (b.flight <= 0) continue;
    if (Math.floor(b.x) === cx && Math.floor(b.y) === cy) return b;
  }
  return null;
}

export function playerCell(p: PlayerState): [number, number] {
  return [Math.floor(p.x), Math.floor(p.y)];
}

/* -------------------------------------------------------------------------- */
/* Movement + collision (shared with the client-side predictor)                */
/* -------------------------------------------------------------------------- */

/** The minimum world an entity needs in order to move and collide. */
export interface CollisionWorld {
  width: number;
  height: number;
  grid: Uint8Array;
  bombs: Array<{ x: number; y: number; flight: number; noCollide: string[] }>;
}

function solidTile(world: CollisionWorld, cx: number, cy: number): number {
  if (cx < 0 || cy < 0 || cx >= world.width || cy >= world.height) return Tile.HARD;
  return world.grid[cy * world.width + cx];
}

/** True when a collision box centred on (x, y) overlaps nothing solid. */
export function canStandAt(
  world: CollisionWorld,
  x: number,
  y: number,
  id: string,
  ghost: boolean,
): boolean {
  const x0 = Math.floor(x - PLAYER_RADIUS);
  const x1 = Math.floor(x + PLAYER_RADIUS);
  const y0 = Math.floor(y - PLAYER_RADIUS);
  const y1 = Math.floor(y + PLAYER_RADIUS);
  for (let cy = y0; cy <= y1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      const tile = solidTile(world, cx, cy);
      if (tile === Tile.HARD) return false;
      if (tile === Tile.SOFT && !ghost) return false;
    }
  }
  return blockingBombAt(world, x, y, id) === null;
}

export function overlapsBomb(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  const reach = PLAYER_RADIUS + 0.5 - EPS;
  return Math.abs(a.x - b.x) < reach && Math.abs(a.y - b.y) < reach;
}

/** The bomb overlapping (x, y) that `id` is not allowed to pass through. */
export function blockingBombAt(
  world: CollisionWorld,
  x: number,
  y: number,
  id: string,
): CollisionWorld['bombs'][number] | null {
  for (const b of world.bombs) {
    if (b.flight > 0 || b.noCollide.includes(id)) continue;
    if (overlapsBomb(b, { x, y })) return b;
  }
  return null;
}

export interface MoveResult {
  /** Set when forward motion was stopped by a kickable bomb. */
  blockedBy: CollisionWorld['bombs'][number] | null;
}

/**
 * Moves an entity along a single axis with lane auto-alignment and corner slip.
 *
 * The alignment pass is what makes rounding corners forgiving: rather than
 * requiring pixel-perfect positioning, a player drifting down a corridor is
 * continuously nudged toward the centre of the lane they are travelling in.
 * Corner slip then covers the remaining case where a small perpendicular nudge
 * would open the path.
 */
export function moveEntity(
  world: CollisionWorld,
  ent: { x: number; y: number },
  id: string,
  ghost: boolean,
  ax: number,
  ay: number,
  dist: number,
): MoveResult {
  const horizontal = ax !== 0;
  const sign = horizontal ? Math.sign(ax) : Math.sign(ay);
  const alignStep = dist * ALIGN_FACTOR;

  if (horizontal) {
    const target = Math.floor(ent.y) + 0.5;
    const delta = target - ent.y;
    if (Math.abs(delta) > EPS) {
      const move = Math.sign(delta) * Math.min(Math.abs(delta), alignStep);
      if (canStandAt(world, ent.x, ent.y + move, id, ghost)) ent.y += move;
    }
  } else {
    const target = Math.floor(ent.x) + 0.5;
    const delta = target - ent.x;
    if (Math.abs(delta) > EPS) {
      const move = Math.sign(delta) * Math.min(Math.abs(delta), alignStep);
      if (canStandAt(world, ent.x + move, ent.y, id, ghost)) ent.x += move;
    }
  }

  const nx = horizontal ? ent.x + sign * dist : ent.x;
  const ny = horizontal ? ent.y : ent.y + sign * dist;
  if (canStandAt(world, nx, ny, id, ghost)) {
    ent.x = nx;
    ent.y = ny;
    return { blockedBy: null };
  }

  const blocker = blockingBombAt(world, nx, ny, id);
  if (blocker) return { blockedBy: blocker };

  const slip = dist * CORNER_SLIP_FACTOR;
  for (const s of [1, -1]) {
    const sx = horizontal ? ent.x : ent.x + s * slip;
    const sy = horizontal ? ent.y + s * slip : ent.y;
    if (!canStandAt(world, sx, sy, id, ghost)) continue;
    const tx = horizontal ? sx + sign * dist : sx;
    const ty = horizontal ? sy : sy + sign * dist;
    if (canStandAt(world, tx, ty, id, ghost)) {
      ent.x = sx;
      ent.y = sy;
      return { blockedBy: null };
    }
  }
  return { blockedBy: null };
}

/** Resolves diagonal intent into a single axis, arcade style. */
export function resolveAxis(
  world: CollisionWorld,
  ent: { x: number; y: number },
  id: string,
  ghost: boolean,
  dir: number,
  mx: number,
  my: number,
  dist: number,
): [number, number] {
  if (mx === 0 || my === 0) return [mx, my];
  const canX = canStandAt(world, ent.x + Math.sign(mx) * dist, ent.y, id, ghost);
  const canY = canStandAt(world, ent.x, ent.y + Math.sign(my) * dist, id, ghost);
  if (canX && !canY) return [mx, 0];
  if (canY && !canX) return [0, my];
  return dir === Dir.LEFT || dir === Dir.RIGHT ? [mx, 0] : [0, my];
}

/** Walk speed in tiles/second for a given speed level. */
export function speedForLevel(level: number): number {
  return BASE_SPEED + level * SPEED_STEP;
}

/* -------------------------------------------------------------------------- */
/* Construction                                                                */
/* -------------------------------------------------------------------------- */

export function createPlayer(seed: PlayerSeed, lives: number, hp = MAX_HEALTH): PlayerState {
  const g = seed.gear;
  const clamp = (v: number | undefined, lo: number, hi: number, fallback: number) =>
    v === undefined ? fallback : Math.max(lo, Math.min(hi, Math.floor(v)));
  return {
    id: seed.id,
    name: seed.name,
    slot: seed.slot,
    team: seed.team ?? seed.slot,
    bot: seed.bot ?? false,
    botLevel: seed.botLevel ?? 1,
    x: 0,
    y: 0,
    dir: Dir.DOWN,
    moving: false,
    alive: true,
    lives,
    respawnAt: 0,
    maxBombs: clamp(g?.maxBombs, START_BOMBS, MAX_BOMBS, START_BOMBS),
    bombsActive: 0,
    power: clamp(g?.power, START_POWER, MAX_POWER, START_POWER),
    speedLevel: clamp(g?.speedLevel, 0, MAX_SPEED_LEVEL, 0),
    kick: g?.kick ?? false,
    punch: g?.punch ?? false,
    remote: g?.remote ?? false,
    pierce: g?.pierce ?? false,
    carryUntil: 0,
    carryVx: 0,
    carryVy: 0,
    launched: false,
    invulnUntil: SPAWN_INVULN_TICKS,
    shieldUntil: 0,
    ghostUntil: 0,
    curse: Curse.NONE,
    curseUntil: 0,
    charged: false,
    hp,
    resolve: 0,
    kills: 0,
    deaths: 0,
    score: 0,
    prevBomb: false,
    prevAction: false,
  };
}

/**
 * The authoritative game. One instance lives in the Durable Object; the client
 * runs a second instance for prediction and a third for fully offline play.
 */
/** The two turns and the reverse for each direction, indexed by `Dir`. */
const TURNS: ReadonlyArray<readonly DirValue[]> = [
  [Dir.LEFT, Dir.RIGHT], // DOWN
  [Dir.DOWN, Dir.UP], // LEFT
  [Dir.DOWN, Dir.UP], // RIGHT
  [Dir.LEFT, Dir.RIGHT], // UP
];
const OPPOSITE: ReadonlyArray<DirValue> = [Dir.UP, Dir.RIGHT, Dir.LEFT, Dir.DOWN];

/**
 * What each species is, as numbers.
 *
 * Speeds are all under the player's `BASE_SPEED` of 3.6, without exception and
 * on purpose: a creature you cannot outrun is a creature you can only kill by
 * getting lucky, and the whole appeal of the fight is that running is always
 * an option you can choose badly. Difficulty comes from how many there are and
 * how they cover the board, not from raw pace.
 */
const ENEMY_SPECIES: Record<
  EnemyKindValue,
  { speed: number; hp: number; ghost: boolean; splits: number }
> = {
  [EnemyKind.DRIFTER]: { speed: 2.4, hp: 1, ghost: false, splits: 0 },
  [EnemyKind.STALKER]: { speed: 1.9, hp: 1, ghost: false, splits: 0 },
  // Slowest, because it is the one you cannot put a wall between yourself and.
  [EnemyKind.PHASER]: { speed: 1.25, hp: 2, ghost: true, splits: 0 },
  /*
   * The brooder chases, always, from anywhere on the board -- there is nowhere
   * to wait it out, which is what makes the last room a fight rather than a
   * stalling contest. Slow enough to outrun at a walk, six hits deep, and it
   * leaves four drifters behind it.
   */
  [EnemyKind.BROODER]: { speed: 1.6, hp: 6, ghost: false, splits: 4 },
};

export function createEnemy(
  id: string,
  kind: EnemyKindValue,
  x: number,
  y: number,
  rng: Rng,
): EnemyState {
  const spec = ENEMY_SPECIES[kind];
  return {
    id,
    kind,
    x,
    y,
    // Rolled, so a row of drifters does not open the room marching in step.
    dir: rng.pick([Dir.DOWN, Dir.LEFT, Dir.RIGHT, Dir.UP]),
    alive: true,
    hp: spec.hp,
    hunted: false,
    speed: spec.speed,
    hurtUntil: 0,
  };
}

/** Walking and carried motion, shared by authority and fixed-tick prediction. */
export function movePlayer(s: GameState, p: PlayerState, input: Input, tick: number, dt = DT,
  move = (ax: number, ay: number, dist: number) => { moveEntity(s, p, p.id, tick < p.ghostUntil, ax, ay, dist); }): boolean {
  if (p.curseUntil && tick >= p.curseUntil) { p.curse = Curse.NONE; p.curseUntil = 0; }
  let mx = input.mx;
  let my = input.my;
  if (p.curse === Curse.REVERSE) {
    mx = -mx;
    my = -my;
  }

  const ghost = tick < p.ghostUntil;
  const before = { x: p.x, y: p.y };

  /*
   * Being thrown overrides asking to walk.
   *
   * A launch that the player could steer out of is a shove, and the vent is
   * meant to be a thing that happens *to* you -- the whole reason it is
   * interesting is that it takes the decision away for a moment. Ice is the
   * opposite and reads through the same field: the carry keeps going, but
   * input is still applied on top, so a slide is something you fight.
   */
  if (p.launched && tick < p.carryUntil) {
    move(Math.sign(p.carryVx), 0, Math.abs(p.carryVx) * dt);
    move(0, Math.sign(p.carryVy), Math.abs(p.carryVy) * dt);
    p.moving = true;
    return true;
  }
  if (tick >= p.carryUntil) p.launched = false;

  const speed = playerSpeed(p) * hazardSpeed(hazardUnder(s, p));
  const dist = speed * dt;

  // Resolve diagonal intent down to a single axis, Bomberman-style.
  const [ax, ay] = resolveAxis(s, p, p.id, ghost, p.dir, mx, my, dist);

  if (ax !== 0) {
    p.dir = ax > 0 ? Dir.RIGHT : Dir.LEFT;
    move(ax, 0, dist);
  } else if (ay !== 0) {
    p.dir = ay > 0 ? Dir.DOWN : Dir.UP;
    move(0, ay, dist);
  }

  /*
   * Ice: you keep going after you stop asking to.
   *
   * Refreshed while there is input and spent when there is not, so letting go
   * on ice carries you on rather than stopping you dead. Fights the player's
   * own input rather than replacing it, which is what makes it feel slippery
   * instead of broken -- you can still turn, you just arrive late.
   */
  const under = hazardUnder(s, p);
  if (under === Hazard.SLICK && (ax !== 0 || ay !== 0)) {
    p.carryUntil = tick + SLIDE_TICKS;
    p.carryVx = ax * speed * SLIDE_FACTOR;
    p.carryVy = ay * speed * SLIDE_FACTOR;
    p.launched = false;
  } else if (!p.launched && tick < p.carryUntil) {
    move(Math.sign(p.carryVx), 0, Math.abs(p.carryVx) * dt);
    move(0, Math.sign(p.carryVy), Math.abs(p.carryVy) * dt);
  }

  p.moving = Math.abs(p.x - before.x) + Math.abs(p.y - before.y) > dist * 0.05;

  return false;
}

function playerSpeed(p: PlayerState): number {
  const speed = speedForLevel(p.speedLevel);
  return speed * (p.curse === Curse.SLOW ? 0.55 : p.curse === Curse.HYPER ? 1.75 : 1);
}

function hazardUnder(s: GameState, p: { x: number; y: number }): number {
  const x = Math.floor(p.x), y = Math.floor(p.y);
  return inBounds(s, x, y) ? s.hazards[idxOf(s, x, y)] : Hazard.NONE;
}

/** Hazard motion is predicted; damage remains authoritative. */
export function movePlayerHazard(s: GameState, p: PlayerState, tick: number, dt = DT,
  move = (ax: number, ay: number, dist: number) => { moveEntity(s, p, p.id, tick < p.ghostUntil, ax, ay, dist); }): 'harm' | 'launch' | null {
  if (!p.alive || s.phase === 'sudden-death') return null;
  const cx = Math.floor(p.x), cy = Math.floor(p.y);
  if (!inBounds(s, cx, cy)) return null;
  const cell = idxOf(s, cx, cy), kind = s.hazards[cell];
  if (!hazardLive(kind, cell, tick)) return null;
  if (hazardHarms(kind)) return 'harm';
  if (kind === Hazard.VENT && tick >= p.carryUntil) {
    const dx = cx - s.width / 2, dy = cy - s.height / 2;
    const [vx, vy] = Math.abs(dx) > Math.abs(dy) ? [-(Math.sign(dx) || 1), 0] : [0, -(Math.sign(dy) || 1)];
    p.carryUntil = tick + LAUNCH_TICKS;
    p.carryVx = vx * LAUNCH_SPEED;
    p.carryVy = vy * LAUNCH_SPEED;
    p.launched = true;
    return 'launch';
  }
  const fan = fanVector(kind);
  if (fan) { move(fan[0], 0, FAN_PUSH * dt); move(0, fan[1], FAN_PUSH * dt); }
  return null;
}

export class Sim {
  readonly state: GameState;
  /** Cosmetic side-effects produced by the most recent `step`. */
  events: GameEvent[] = [];

  private rng: Rng;
  private spawns: Array<[number, number]>;
  private spiral: number[];
  private countdown: number;

  checkpoint(): SimCheckpoint {
    return JSON.parse(JSON.stringify({ version: 1, state: { ...this.state,
      grid: [...this.state.grid], items: [...this.state.items], hazards: [...this.state.hazards] },
      rng: this.rng.save(), countdown: this.countdown })) as SimCheckpoint;
  }

  static restore(raw: unknown): Sim | null {
    if (!validCheckpoint(raw)) return null;
    const saved = structuredClone(raw);
    const sim = new Sim(saved.state.settings, saved.state.players);
    if (sim.state.width !== saved.state.width || sim.state.height !== saved.state.height) return null;
    Object.assign(sim.state, saved.state, { grid: Uint8Array.from(saved.state.grid),
      items: Uint8Array.from(saved.state.items), hazards: Uint8Array.from(saved.state.hazards) });
    sim.rng.restore(saved.rng);
    sim.countdown = saved.countdown;
    return sim;
  }

  constructor(settings: MatchSettings, seeds: PlayerSeed[]) {
    const arena = getArena(settings.mapId);
    this.rng = new Rng(settings.seed);
    const built = buildMap(arena, this.rng, arena.softDensity * settings.itemDensity);
    this.spawns = built.spawns;
    this.spiral = spiralOrder(built.width, built.height);
    this.countdown = Math.round(3 * TICK_HZ);

    const players = seeds.map((s) => createPlayer(s, settings.lives, MAX_HEALTH));
    this.state = {
      tick: 0,
      phase: 'countdown',
      width: built.width,
      height: built.height,
      grid: built.grid,
      items: new Uint8Array(built.width * built.height),
      hazards: built.hazards,
      players,
      enemies: [],
      bombs: [],
      blasts: [],
      settings,
      timeLeft: settings.roundSeconds * TICK_HZ,
      sdIndex: 0,
      sdTimer: 0,
      exitAt: -1,
      winners: [],
      wave: 1,
      nextBombId: 1,
      nextBlastId: 1,
    };

    players.forEach((p, i) => {
      const spawn = this.spawns[i % this.spawns.length];
      p.x = spawn[0] + 0.5;
      p.y = spawn[1] + 0.5;
      p.dir = spawn[1] < built.height / 2 ? Dir.DOWN : Dir.UP;
    });

    if (settings.enemies?.length) this.placeEnemies(settings.enemies, players.length);
    if (settings.roomGoal === RoomGoal.EXIT) this.placeExit();
    if (settings.roomGoal === RoomGoal.HUNT) this.markQuarry();
  }

  /**
   * Puts the exit somewhere worth walking to.
   *
   * Furthest walkable tile from the player, by Manhattan distance. An exit
   * three tiles from the spawn is not a room, it is a corridor -- the objective
   * has to cost the same crossing the room would.
   *
   * On open floor rather than under a soft block, deliberately. Hiding it is
   * the series convention and it is the wrong convention here: a run is eight
   * rooms deep and a player who cannot find the exit loses the whole run to a
   * search, which is exactly the failure reviewers named in Bomberman Quest.
   */
  private placeExit(): void {
    const s = this.state;
    let best = -1;
    let bestDist = -1;
    for (let y = 0; y < s.height; y++) {
      for (let x = 0; x < s.width; x++) {
        if (tileAt(s, x, y) !== Tile.EMPTY) continue;
        const d = Math.min(...s.players.map((p) => Math.abs(x + 0.5 - p.x) + Math.abs(y + 0.5 - p.y)));
        if (d > bestDist) {
          bestDist = d;
          best = idxOf(s, x, y);
        }
      }
    }
    s.exitAt = best;
  }

  /**
   * Marks the one creature a `hunt` room is about.
   *
   * The furthest one, for the same reason the exit is furthest: the objective
   * should cost a crossing. A phaser is never chosen -- it walks through soft
   * blocks, so the one creature you are required to reach would be the one
   * that can stand somewhere you cannot follow.
   */
  private markQuarry(): void {
    const s = this.state;
    const eligible = s.enemies.filter((e) => e.kind !== EnemyKind.PHASER);
    const pool = eligible.length ? eligible : s.enemies;
    let best: EnemyState | null = null;
    let bestDist = -1;
    for (const e of pool) {
      const d = Math.min(...s.players.map((p) => Math.abs(e.x - p.x) + Math.abs(e.y - p.y)));
      if (d > bestDist) {
        bestDist = d;
        best = e;
      }
    }
    if (best) {
      best.hunted = true;
      // Two blasts, so it cannot be taken by a stray arm on the way past. The
      // room is meant to be about reaching it.
      best.hp = Math.max(2, best.hp);
    }
  }

  /**
   * Puts the creatures on the board, as far from the players as the arena has.
   *
   * The spawn markers a room does not seat a player on come first: they are
   * already spread across the layout and `buildMap` clears a pocket around
   * each, so nothing starts inside a soft block or boxed in.
   *
   * After those run out it is open floor, and the order matters more than it
   * looks. Sorting by distance and taking the far end sounds right and is not:
   * on a four-seat arena there are only three spare markers, so a room holding
   * ten creatures would put seven of them in whichever corner is furthest
   * away, which is one fight in a corner rather than a room. So: everything
   * outside a safety radius, shuffled. A creature that opens the room already
   * touching you is not difficulty, it is a hit you were never offered a
   * chance to avoid -- and a creature that opens it in a heap with six others
   * is not a room.
   */
  private placeEnemies(table: readonly EnemySpawn[], seated: number): void {
    const s = this.state;
    const taken = new Set<number>();
    const far = (x: number, y: number) =>
      Math.min(...s.players.map((p) => Math.abs(x - p.x) + Math.abs(y - p.y)));

    const free: Array<[number, number]> = [];
    for (const spawn of this.spawns.slice(seated)) {
      if (tileAt(s, spawn[0], spawn[1]) === Tile.EMPTY) free.push(spawn);
    }

    const rest: Array<[number, number]> = [];
    for (let y = 0; y < s.height; y++) {
      for (let x = 0; x < s.width; x++) {
        if (tileAt(s, x, y) !== Tile.EMPTY) continue;
        if (free.some((c) => c[0] === x && c[1] === y)) continue;
        rest.push([x, y]);
      }
    }
    /*
     * Open floor is not the same as somewhere a creature can walk.
     *
     * `buildMap` scatters soft blocks over the layout, and a single empty tile
     * boxed in by four of them is a common shape. A creature placed in one is
     * alive, unreachable and completely still until the player happens to bomb
     * its way in -- and it still has to die before the room clears. Measured on
     * the first driven room: one of three creatures moved. So a candidate
     * needs somewhere to go.
     */
    const canWalk = (x: number, y: number) =>
      DIR_VECTORS.some(([dx, dy]) => tileAt(s, x + dx, y + dy) === Tile.EMPTY);
    const safe = rest.filter(
      (c) => far(c[0] + 0.5, c[1] + 0.5) >= ENEMY_SPAWN_CLEARANCE && canWalk(c[0], c[1]),
    );
    // An arena too small to hold the radius still gets creatures, placed as
    // far off as it has room for. A short room beats a room with nothing in it.
    const spread = safe.length ? this.rng.shuffle(safe) : rest.sort((a, b) => far(b[0], b[1]) - far(a[0], a[1]));
    const cells = [...free, ...spread];

    let n = 0;
    for (const { kind, count } of table) {
      for (let i = 0; i < count; i++) {
        const cell = cells.find((c) => !taken.has(idxOf(s, c[0], c[1])));
        // An arena with nowhere left to stand simply gets a smaller room.
        if (!cell) return;
        taken.add(idxOf(s, cell[0], cell[1]));
        s.enemies.push(createEnemy(`e${n++}`, kind, cell[0] + 0.5, cell[1] + 0.5, this.rng));
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Main loop                                                           */
  /* ------------------------------------------------------------------ */

  step(inputs: Map<string, Input>): GameEvent[] {
    this.events = [];
    const s = this.state;
    s.tick++;

    if (s.phase === 'countdown') {
      this.countdown--;
      if (this.countdown <= 0) {
        s.phase = 'playing';
        this.events.push({ t: 'phase', phase: 'playing' });
      }
      // Bombs and players are frozen, but blasts left over from a previous
      // round still need to tick out.
      this.stepBlasts();
      return this.events;
    }

    if (s.phase === 'over') {
      this.stepBlasts();
      return this.events;
    }

    for (const p of s.players) {
      this.stepPlayer(p, inputs.get(p.id) ?? EMPTY_INPUT);
    }
    this.stepEnemies();
    this.stepHazards();
    this.stepBombs();
    this.stepBlasts();
    this.stepPickups();
    this.updateResolve();
    this.sweepCharges();
    this.stepTimer();
    this.checkVictory();

    return this.events;
  }

  /* ------------------------------------------------------------------ */
  /* Players                                                             */
  /* ------------------------------------------------------------------ */

  private stepPlayer(p: PlayerState, rawInput: Input): void {
    const s = this.state;

    if (!p.alive) {
      if (p.lives > 0 && s.tick >= p.respawnAt) this.respawn(p);
      return;
    }

    if (p.curseUntil && s.tick >= p.curseUntil) {
      p.curse = Curse.NONE;
      p.curseUntil = 0;
    }

    const launched = movePlayer(s, p, rawInput, s.tick, DT, (ax, ay, dist) => this.moveAxis(p, ax, ay, dist));
    if (launched) {
      this.releaseBombOverlaps(p);
      p.prevBomb = rawInput.bomb;
      p.prevAction = rawInput.action;
      return;
    }

    // Bombs the player has fully stepped off of become solid again.
    this.releaseBombOverlaps(p);

    const wantBomb = (rawInput.bomb && !p.prevBomb) || p.curse === Curse.AUTO_BOMB;
    if (wantBomb) this.tryPlaceBomb(p);
    if (rawInput.action && !p.prevAction) this.tryAction(p);
    p.prevBomb = rawInput.bomb;
    p.prevAction = rawInput.action;
  }

  /** Moves a player one axis and turns a blocked bomb into a kick. */
  private moveAxis(p: PlayerState, ax: number, ay: number, dist: number): void {
    const s = this.state;
    const ghost = s.tick < p.ghostUntil;
    const { blockedBy } = moveEntity(s, p, p.id, ghost, ax, ay, dist);
    if (!blockedBy || !p.kick) return;

    const bomb = blockedBy as BombState;
    if (bomb.vx !== 0 || bomb.vy !== 0) return;
    const dx = ax !== 0 ? Math.sign(ax) : 0;
    const dy = ay !== 0 ? Math.sign(ay) : 0;
    if (!this.canBombEnter(bomb, Math.floor(bomb.x) + dx, Math.floor(bomb.y) + dy)) return;
    bomb.vx = dx * KICK_SPEED;
    bomb.vy = dy * KICK_SPEED;
    this.events.push({ t: 'kick', x: bomb.x, y: bomb.y });
  }

  /** True when a player's collision box at (x, y) overlaps nothing solid. */
  canStand(p: PlayerState, x: number, y: number): boolean {
    return canStandAt(this.state, x, y, p.id, this.state.tick < p.ghostUntil);
  }

  private releaseBombOverlaps(p: PlayerState): void {
    for (const b of this.state.bombs) {
      const i = b.noCollide.indexOf(p.id);
      if (i === -1) continue;
      if (!overlapsBomb(b, p)) b.noCollide.splice(i, 1);
    }
  }

  private respawn(p: PlayerState): void {
    const s = this.state;
    const home = this.spawns[p.slot % this.spawns.length];
    const candidates = [home, ...this.spawns].filter(Boolean);
    let chosen: readonly [number, number] | null = null;
    for (const c of candidates) {
      if (tileAt(s, c[0], c[1]) === Tile.EMPTY && !bombAt(s, c[0], c[1])) {
        chosen = c;
        break;
      }
    }

    /*
     * Anywhere open, if no spawn is.
     *
     * This used to fall back to `home` whatever was standing on it, which in
     * sudden death is a wall. Measured across 60 elimination matches on the
     * shipped rules, that left 57 players alive *inside* solid tiles -- and
     * once there they are stuck for the rest of the match: the spiral skips
     * tiles that are already solid so they can never be crushed, and
     * `canStandAt` samples a box that overlaps the wall they are in so they
     * can never walk out. A live player who cannot act and cannot be removed
     * also keeps last-one-standing from firing.
     *
     * Nearest to home so a respawn still means something positional, rather
     * than teleporting somebody across the arena.
     */
    if (!chosen) {
      let best = Infinity;
      for (let y = 0; y < s.height; y++) {
        for (let x = 0; x < s.width; x++) {
          if (tileAt(s, x, y) !== Tile.EMPTY || bombAt(s, x, y)) continue;
          const d = Math.abs(x - home[0]) + Math.abs(y - home[1]);
          if (d < best) {
            best = d;
            chosen = [x, y];
          }
        }
      }
    }

    /*
     * And if the arena has closed completely, they do not come back at all.
     *
     * `stepPlayer` retries every tick once the timer has passed, so this is a
     * wait rather than a forfeit -- but in a closing arena the wait does not
     * end, which is what sudden death is for. Better than the alternative of
     * standing them in a wall to be counted as alive.
     */
    if (!chosen) return;
    p.x = chosen[0] + 0.5;
    p.y = chosen[1] + 0.5;
    p.alive = true;
    p.hp = MAX_HEALTH;
    /*
     * Longer protection the more this player has already been killed.
     *
     * Kill upkeep: the same advantage is worth less each time it is pressed. A
     * player being farmed comes back harder to farm. Derived from `deaths`,
     * which is already in player state and already on the wire, so there is no
     * new field to keep in step between the browser's prediction and the
     * room's authority.
     */
    const farmed = Math.min(Math.max(0, p.deaths - 1), FARM_INVULN_MAX_DEATHS);
    p.invulnUntil = s.tick + Math.round(SPAWN_INVULN_TICKS * (1 + farmed * FARM_INVULN_PER_DEATH));
    p.curse = Curse.NONE;
    p.curseUntil = 0;
    // An Overload for whoever came back behind. See `grantCharge`.
    this.grantCharge(p);
  }

  /* ------------------------------------------------------------------ */
  /* Hazards                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * One tick of whatever the arena is doing to everyone.
   *
   * Runs after the players and creatures have moved and before the bombs, so a
   * fan acts on where things ended up this tick rather than where they started
   * -- and so a bomb pushed by a fan is stepped by the bomb code in the same
   * tick, using the machinery kicking already built rather than a second
   * mover that could disagree with it.
   */
  private stepHazards(): void {
    const s = this.state;
    if (!s.hazards.length) return;
    /*
     * The arena stops helping once the arena is the one deciding.
     *
     * Sudden death exists to settle a match the clock could not, and it does
     * that by closing the walls in. A wire firing underneath that is a second
     * thing deciding the same match, and it shows up as draws: across sixteen
     * drawn deathmatches driven to the walls, matches ending with exactly one
     * winner fell from sixteen to fourteen once hazards ran through the
     * tiebreak, because two players now die on the same tick to two different
     * causes.
     *
     * Turning them off is also the better rule to play. The walls take
     * everything and cannot be survived; that is the point of the phase, and
     * a hazard that might get there first only makes the ending noisier.
     */
    if (s.phase === 'sudden-death') return;

    for (const p of s.players) {
      const effect = movePlayerHazard(s, p, s.tick, DT, (ax, ay, dist) => this.moveAxis(p, ax, ay, dist));
      if (effect === 'harm' && s.tick >= p.invulnUntil && s.tick >= p.shieldUntil) {
        this.events.push({ t: 'zap', x: p.x, y: p.y, player: p.id });
        this.damagePlayer(p, null);
      } else if (effect === 'launch') {
        this.events.push({ t: 'launch', x: p.x, y: p.y, player: p.id });
      }
    }

    /*
     * Bombs and pickups ride the fans too, and nothing else does.
     *
     * Crates stay put on purpose -- they are the board, and a fan that
     * rearranged the walls would make every arena a different arena by the
     * thirty-second mark. What a fan is *for* is making a thrown bomb somebody
     * else's problem, which is exactly the two object types that move.
     */
    for (const b of s.bombs) {
      if (b.flight > 0) continue;
      const cell = idxOf(s, Math.floor(b.x), Math.floor(b.y));
      const kind = s.hazards[cell];
      if (!hazardLive(kind, cell, s.tick)) continue;
      const fan = fanVector(kind);
      // Reusing the kick velocity rather than moving it by hand, so a bomb in
      // a draught collides, chains and stops against walls the way a kicked
      // one already does.
      if (fan) {
        b.vx = fan[0] * KICK_SPEED * FAN_BOMB;
        b.vy = fan[1] * KICK_SPEED * FAN_BOMB;
      } else if (kind === Hazard.VENT) {
        b.vx = 0;
        b.vy = 0;
      }
    }

    this.driftItems();
  }

  /**
   * Pickups blown along a fan lane, at most one cell a step.
   *
   * Items live in a flat overlay rather than as entities, so "pushing" one is
   * moving a byte -- and it can only move into a cell that is empty floor with
   * nothing already on it, which keeps the overlay single-valued without
   * needing a collision pass.
   *
   * Rate-limited to a step every `ITEM_DRIFT_TICKS`, because at tick rate a
   * pickup would cross the board faster than a player can chase it, and the
   * point is to make a power-up somewhere awkward, not to delete it.
   */
  private driftItems(): void {
    const s = this.state;
    if (s.tick % ITEM_DRIFT_TICKS !== 0) return;
    const before = s.items.slice();
    for (let cell = 0; cell < s.items.length; cell++) {
      if (before[cell] === Item.NONE || s.grid[cell] !== Tile.EMPTY) continue;
      const kind = s.hazards[cell];
      if (!hazardLive(kind, cell, s.tick)) continue;
      const fan = fanVector(kind);
      if (!fan) continue;
      const nx = (cell % s.width) + fan[0];
      const ny = Math.floor(cell / s.width) + fan[1];
      if (!inBounds(s, nx, ny)) continue;
      const next = idxOf(s, nx, ny);
      if (s.grid[next] !== Tile.EMPTY || before[next] !== Item.NONE || s.items[next] !== Item.NONE) continue;
      s.items[next] = s.items[cell];
      s.items[cell] = Item.NONE;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Creatures                                                           */
  /* ------------------------------------------------------------------ */

  private stepEnemies(): void {
    const s = this.state;
    if (!s.enemies.length) return;
    for (const e of s.enemies) {
      if (!e.alive) continue;
      this.moveEnemy(e);
      this.touchPlayers(e);
    }
  }

  /**
   * One creature's tick. The species is the movement, and nothing else.
   *
   * Deliberately not the bot planner. `botThink` builds a danger map, runs a
   * flood fill and picks a plan, because it is playing the same game as you.
   * A creature is not: it cannot place a bomb, cannot pick anything up, and
   * has no notion of safety. Which is what makes it *readable* -- a player
   * learns a drifter in one room, and that knowledge is worth something in
   * every room after it. A creature that outthought you would just be a bot.
   */
  private moveEnemy(e: EnemyState): void {
    const s = this.state;
    const ghost = ENEMY_SPECIES[e.kind].ghost;
    const dist = e.speed * DT;

    const chaser = e.kind !== EnemyKind.DRIFTER;
    let [ax, ay] = DIR_VECTORS[e.dir];
    let alt: [number, number] | null = null;
    if (chaser) {
      const hunted = this.nearestPlayer(e, e.kind === EnemyKind.STALKER ? STALKER_SIGHT : Infinity);
      if (hunted) {
        /*
         * Chase on the wider axis, and keep the other one as a fallback.
         *
         * Single-axis, like everything else that moves in this game -- a
         * creature sliding diagonally would be the only thing on the board
         * that could, and it would look wrong next to a player who cannot.
         *
         * The fallback is what stops it grinding. Without it a stalker with a
         * pillar between itself and the player pushes into that pillar for as
         * long as the player stands there: the chase recomputes the same
         * blocked direction every tick, so the random turn below never
         * survives to be acted on. One perpendicular retry is not pathfinding,
         * and is not meant to be -- it is enough to get around a wall, which
         * is the difference between a creature that arrives and one that
         * leans on a corner.
         */
        const dx = hunted.x - e.x;
        const dy = hunted.y - e.y;
        if (Math.abs(dx) > Math.abs(dy)) {
          [ax, ay] = [Math.sign(dx), 0];
          if (dy !== 0) alt = [0, Math.sign(dy)];
        } else {
          [ax, ay] = [0, Math.sign(dy)];
          if (dx !== 0) alt = [Math.sign(dx), 0];
        }
      }
    }

    const before = { x: e.x, y: e.y };
    moveEntity(s, e, e.id, ghost, ax, ay, dist);
    let stuck = Math.abs(e.x - before.x) + Math.abs(e.y - before.y) < dist * 0.05;
    if (stuck && alt) {
      moveEntity(s, e, e.id, ghost, alt[0], alt[1], dist);
      if (Math.abs(e.x - before.x) + Math.abs(e.y - before.y) >= dist * 0.05) {
        [ax, ay] = alt;
        stuck = false;
      }
    }
    if (ax !== 0) e.dir = ax > 0 ? Dir.RIGHT : Dir.LEFT;
    else if (ay !== 0) e.dir = ay > 0 ? Dir.DOWN : Dir.UP;

    /*
     * Blocked, so turn -- toward somewhere that is actually open.
     *
     * Picking blind from the other three directions is what the first cut did,
     * and it does not wander, it vibrates: two of the three are usually walls,
     * so the creature re-rolls next tick and the cheapest escape is the way it
     * came. Measured over three seconds on neon-grid, four drifters each walked
     * 7.2 tiles and finished 0.4 tiles from where they started. A room full of
     * those is not alive, it is a board the player has to sweep.
     *
     * So: only directions it can actually enter, and turns before reverses. A
     * dead end still reverses, because in a dead end that is the only honest
     * answer -- which is also what the series does.
     *
     * Rolled from the sim's own `rng`, so a replay of the seed reproduces it.
     * `Math.random` here would make every capture tool in the repo
     * non-deterministic again.
     */
    if (stuck) {
      const back = OPPOSITE[e.dir];
      const open = TURNS[e.dir].filter((d) => {
        const [tx, ty] = DIR_VECTORS[d];
        return canStandAt(s, e.x + tx * dist, e.y + ty * dist, e.id, ghost);
      });
      if (open.length) e.dir = this.rng.pick(open);
      else e.dir = back;
    }
  }

  /** The closest live player within `range`, or null. */
  private nearestPlayer(e: EnemyState, range: number): PlayerState | null {
    let best: PlayerState | null = null;
    let bestDist = range;
    for (const p of this.state.players) {
      if (!p.alive) continue;
      const d = Math.abs(p.x - e.x) + Math.abs(p.y - e.y);
      if (d <= bestDist) {
        bestDist = d;
        best = p;
      }
    }
    return best;
  }

  /**
   * Contact damage: the creature's only attack.
   *
   * Routed through `damagePlayer` rather than straight to a kill, so hit
   * points, resolve and the post-hit grace all behave exactly as they do for a
   * blast. `null` as the killer because there is no player to credit -- a
   * creature is not a scoring opponent, and handing it a `PlayerState` purely
   * to have something to blame is how the kill counters would start lying.
   */
  private touchPlayers(e: EnemyState): void {
    const s = this.state;
    for (const p of s.players) {
      if (!p.alive) continue;
      if (s.tick < p.invulnUntil || s.tick < p.shieldUntil) continue;
      if (s.tick < p.ghostUntil) continue;
      if (Math.abs(p.x - e.x) > ENEMY_TOUCH_RANGE || Math.abs(p.y - e.y) > ENEMY_TOUCH_RANGE) continue;
      this.damagePlayer(p, null);
    }
  }

  /** A blast reaching a creature. Called from `applyBlastDamage`. */
  private hurtEnemy(e: EnemyState): void {
    const s = this.state;
    if (!e.alive || s.tick < e.hurtUntil) return;
    e.hp -= 1;
    if (e.hp > 0) {
      e.hurtUntil = s.tick + ENEMY_HURT_TICKS;
      this.events.push({ t: 'enemy-hurt', id: e.id, x: e.x, y: e.y, kind: e.kind });
      return;
    }
    e.alive = false;
    this.events.push({ t: 'enemy-death', id: e.id, x: e.x, y: e.y, kind: e.kind });
    this.splitEnemy(e);
  }

  /**
   * What a brooder leaves behind.
   *
   * The room gets worse before it ends, which is the only thing that makes a
   * last hit land. Placed around the corpse rather than on it, on tiles that
   * are actually open -- a brood spawned inside the wall it died against would
   * be unreachable, and in a `clear` room unreachable means unfinishable.
   *
   * They inherit nothing. A brood of brooders would never terminate, and the
   * `splits` field being read from the *spawned* species rather than the
   * parent is what guarantees that structurally rather than by promise.
   */
  private splitEnemy(parent: EnemyState): void {
    const spec = ENEMY_SPECIES[parent.kind];
    if (!spec.splits) return;
    const s = this.state;
    const cx = Math.floor(parent.x);
    const cy = Math.floor(parent.y);
    const around: Array<[number, number]> = [];
    for (let r = 0; r <= 2 && around.length < spec.splits; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = cx + dx;
          const y = cy + dy;
          if (!inBounds(s, x, y) || tileAt(s, x, y) !== Tile.EMPTY) continue;
          around.push([x, y]);
        }
      }
    }
    for (let i = 0; i < spec.splits && i < around.length; i++) {
      const [x, y] = around[i];
      s.enemies.push(createEnemy(`${parent.id}s${i}`, EnemyKind.DRIFTER, x + 0.5, y + 0.5, this.rng));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bombs                                                               */
  /* ------------------------------------------------------------------ */

  private tryPlaceBomb(p: PlayerState): void {
    const s = this.state;
    if (p.curse === Curse.NO_BOMB) return;
    const active = s.bombs.filter((b) => b.ownerId === p.id).length;
    if (active >= p.maxBombs) return;

    const cx = Math.floor(p.x);
    const cy = Math.floor(p.y);
    if (tileAt(s, cx, cy) !== Tile.EMPTY) return;
    if (bombAt(s, cx, cy)) return;

    /*
     * Spending an Overload.
     *
     * A curse still wins: TINY_FLAME is the counterplay, and a player who
     * picked up a skull has a problem the charge does not solve. Anything else
     * would make the comeback mechanic also a curse-removal mechanic, which is
     * two features wearing one coat.
     */
    const overload = p.charged && p.curse !== Curse.TINY_FLAME;
    if (overload) p.charged = false;
    const power = p.curse === Curse.TINY_FLAME ? 1 : overload ? FULL_FIRE_POWER : p.power;
    const bomb: BombState = {
      id: s.nextBombId++,
      ownerId: p.id,
      x: cx + 0.5,
      y: cy + 0.5,
      fuse: p.remote
        ? Number.MAX_SAFE_INTEGER
        : p.curse === Curse.SHORT_FUSE
          ? SHORT_FUSE_TICKS
          : BOMB_FUSE_TICKS,
      power,
      pierce: p.pierce || overload,
      remote: p.remote,
      vx: 0,
      vy: 0,
      flight: 0,
      flightFrom: [cx + 0.5, cy + 0.5],
      flightTo: [cx + 0.5, cy + 0.5],
      noCollide: [p.id],
    };
    // Anyone already standing on the tile gets to walk off it.
    for (const other of s.players) {
      if (other.id === p.id || !other.alive) continue;
      if (Math.abs(other.x - bomb.x) < PLAYER_RADIUS + 0.5 && Math.abs(other.y - bomb.y) < PLAYER_RADIUS + 0.5) {
        bomb.noCollide.push(other.id);
      }
    }
    s.bombs.push(bomb);
    p.bombsActive = active + 1;
    this.events.push({ t: 'bomb', x: bomb.x, y: bomb.y, owner: p.id });
    // Announced separately so the renderer and the HUD can both react without
    // inspecting bomb stats to guess whether this one was special.
    if (overload) this.events.push({ t: 'overload', player: p.id, x: bomb.x, y: bomb.y });
  }

  /** Secondary action: punch the bomb in front, else trigger remote bombs. */
  private tryAction(p: PlayerState): void {
    const s = this.state;
    const [dx, dy] = DIR_VECTORS[p.dir];
    const front = bombAt(s, Math.floor(p.x) + dx, Math.floor(p.y) + dy);

    /*
     * A bomb in the air can be punched again, if the ruleset allows it.
     *
     * It has to be looked for separately: `bombAt` skips bombs in flight,
     * because every other caller is asking whether a tile is blocked and a
     * bomb overhead does not block anything. The timing window is however long
     * the bomb takes to cross the tile in front of you -- about a sixth of a
     * second -- and hitting it is the skill.
     *
     * No new state and nothing on the wire: a deflection is just another
     * flight, which the snapshot already carries a flag for.
     *
     * A bomb whose fuse has already run out cannot be volleyed again. That is
     * the hard end of a rally: `detonateReady` waits for a bomb to land before
     * it goes off, so without this two players could keep a dead bomb in the
     * air between them for the rest of the match.
     */
    const inAir =
      s.settings.deflect === true ? flyingBombAt(s, Math.floor(p.x) + dx, Math.floor(p.y) + dy) : null;
    const target = front ?? (inAir && inAir.fuse > 0 ? inAir : null);
    if (p.punch && target) {
      const from: [number, number] = [target.x, target.y];
      let tx = Math.floor(target.x);
      let ty = Math.floor(target.y);
      for (let i = 0; i < PUNCH_DISTANCE; i++) {
        tx += dx;
        ty += dy;
      }
      const landing = this.findLanding(target, tx, ty, dx, dy);
      target.flight = PUNCH_TICKS;
      target.flightFrom = from;
      target.flightTo = [landing[0] + 0.5, landing[1] + 0.5];
      target.vx = 0;
      target.vy = 0;
      target.noCollide = [];
      this.events.push({ t: 'punch', x: target.x, y: target.y });
      return;
    }

    if (p.remote) {
      const mine = s.bombs.filter((b) => b.ownerId === p.id && b.remote && b.flight === 0);
      for (const b of mine) b.fuse = 0;
      if (mine.length) this.detonateReady();
    }
  }

  /**
   * Walks forward from a punched bomb's nominal landing tile until it finds a
   * free one, wrapping around the arena like the arcade original.
   */
  private findLanding(bomb: BombState, tx: number, ty: number, dx: number, dy: number): [number, number] {
    const s = this.state;
    for (let guard = 0; guard < s.width * s.height; guard++) {
      let cx = tx;
      let cy = ty;
      if (cx <= 0) cx = s.width - 2;
      if (cx >= s.width - 1) cx = 1;
      if (cy <= 0) cy = s.height - 2;
      if (cy >= s.height - 1) cy = 1;
      tx = cx;
      ty = cy;
      const occupied = tileAt(s, cx, cy) !== Tile.EMPTY || bombAt(s, cx, cy) !== null;
      if (!occupied) return [cx, cy];
      tx += dx;
      ty += dy;
    }
    return [Math.floor(bomb.x), Math.floor(bomb.y)];
  }

  private canBombEnter(bomb: BombState, cx: number, cy: number): boolean {
    const s = this.state;
    if (tileAt(s, cx, cy) !== Tile.EMPTY) return false;
    const other = bombAt(s, cx, cy);
    if (other && other !== bomb) return false;
    for (const p of s.players) {
      if (!p.alive) continue;
      if (Math.floor(p.x) === cx && Math.floor(p.y) === cy) return false;
    }
    return true;
  }

  private stepBombs(): void {
    const s = this.state;

    for (const b of s.bombs) {
      if (b.flight > 0) {
        b.flight--;
        const t = 1 - b.flight / PUNCH_TICKS;
        b.x = b.flightFrom[0] + (b.flightTo[0] - b.flightFrom[0]) * t;
        b.y = b.flightFrom[1] + (b.flightTo[1] - b.flightFrom[1]) * t;
        if (b.flight === 0) {
          b.x = b.flightTo[0];
          b.y = b.flightTo[1];
        }
        /*
         * The fuse runs in the air too.
         *
         * It used to stop, which handed a punched bomb an extra 0.45 seconds
         * of life -- small, and an accidental reward for punching. It is also
         * the only thing that caps a juggle: two players volleying a bomb
         * between them would otherwise do it forever. Now the bomb is on a
         * clock the whole time, so a rally is a rally with a live bomb in it,
         * which is the point.
         */
        if (b.fuse !== Number.MAX_SAFE_INTEGER) b.fuse--;
        continue;
      }

      if (b.vx !== 0 || b.vy !== 0) {
        const dx = Math.sign(b.vx);
        const dy = Math.sign(b.vy);
        const nx = b.x + b.vx * DT;
        const ny = b.y + b.vy * DT;
        // Only commit once the bomb would cross into the next tile.
        const nextCell: [number, number] = [Math.floor(nx + dx * 0.5), Math.floor(ny + dy * 0.5)];
        if (this.canBombEnter(b, nextCell[0], nextCell[1])) {
          b.x = nx;
          b.y = ny;
        } else {
          b.x = Math.floor(b.x) + 0.5;
          b.y = Math.floor(b.y) + 0.5;
          b.vx = 0;
          b.vy = 0;
        }
      }

      if (b.fuse !== Number.MAX_SAFE_INTEGER) b.fuse--;
    }

    this.detonateReady();

    for (const p of s.players) {
      p.bombsActive = s.bombs.filter((b) => b.ownerId === p.id).length;
    }
  }

  /** Detonates every bomb whose fuse has run out, resolving chains to a fixpoint. */
  private detonateReady(): void {
    const s = this.state;
    for (let guard = 0; guard < 256; guard++) {
      const ready = s.bombs.filter((b) => b.fuse <= 0 && b.flight === 0);
      if (!ready.length) return;
      for (const b of ready) {
        const i = s.bombs.indexOf(b);
        if (i >= 0) s.bombs.splice(i, 1);
        this.explode(b);
      }
    }
  }

  /**
   * The drop table as it looks to whoever broke the block.
   *
   * Rolled by the breaker rather than by whoever eventually picks the item up.
   * That is the smaller change -- no protocol field, no "unknown item" sprite
   * -- and it has a property rolling at pickup would not: the player breaking
   * the most blocks is the one seeding the pool, so a runaway leader dilutes
   * the board with commons through the very act of running away. Rolling at
   * pickup would be truer to Mario Kart's item box and is the obvious next
   * step if this is not enough.
   *
   * Deterministic, like everything else here: the standing comes from player
   * state, the roll comes from the sim's own `rng`, and the same inputs
   * produce the same board in the browser, in the Durable Object and offline.
   */
  /**
   * Arm an Overload if this player is materially behind the field.
   *
   * Judged on material rather than on the scoreboard: a player can be losing
   * on kills while holding the strongest loadout on the board, and that player
   * does not need help -- the one who does is the one whose bombs cannot
   * reach. Never stacks: falling behind twice does not arm two bombs, it arms
   * the one you did not spend.
   */
  private grantCharge(p: PlayerState): void {
    if (!this.catchup || p.charged || p.lives <= 0) return;
    const alive = this.state.players.filter((o) => o.lives > 0);
    if (alive.length < 2) return;

    /*
     * Behind on the thing that actually wins, which is not material.
     *
     * This was keyed on `materialPower` and measured as useless, and the
     * attribution probe says why: of everything a player can lead at thirty
     * seconds, kills predict the winner at 57% against a 25% baseline, while
     * range predicts 36% and bomb count 32%. The loop is kill to kill. A
     * comeback aimed at the item economy was aimed at the weakest predictor
     * in the game.
     *
     * Kills net of deaths, against the field average, so a player who is
     * trading evenly does not qualify and a player being farmed does. Material
     * stays in it at a quarter weight -- somebody who is even on kills but
     * holding nothing is still in trouble, just less of it.
     */
    let kd = 0;
    let mat = 0;
    for (const o of alive) {
      kd += o.kills - o.deaths;
      mat += materialPower(o);
    }
    const killDeficit = kd / alive.length - (p.kills - p.deaths);
    const matDeficit = mat / alive.length - materialPower(p);
    if (killDeficit + matDeficit * 0.25 < CHARGE_DEFICIT) return;
    p.charged = true;
    this.events.push({ t: 'charge', player: p.id });
  }

  /**
   * The clock half of the same rule.
   *
   * Respawns alone were measured reaching a player about once a match, which
   * is no use against a gap that forms in the first thirty seconds -- and
   * useless entirely against a player who is losing without dying.
   */
  private sweepCharges(): void {
    if (this.state.tick % (CHARGE_CHECK_SECONDS * TICK_HZ) !== 0) return;
    for (const p of this.state.players) if (p.alive) this.grantCharge(p);
  }

  private dropTableFor(ownerId: string): ReadonlyArray<readonly [ItemValue, number]> {
    if (!this.catchup) return DROP_TABLE;
    const alive = this.state.players.filter((p) => p.lives > 0);
    const owner = alive.find((p) => p.id === ownerId);
    // A bomb whose owner has left, or a solo match: nobody to be ahead of.
    if (!owner || alive.length < 2) return DROP_TABLE;

    let total = 0;
    for (const p of alive) total += materialPower(p);
    const lead = materialPower(owner) - total / alive.length;
    // 0 at the front of the field, 1 at the back, 0.5 when level -- and at
    // 0.5 every multiplier is 1.0, so a level field rolls the base table.
    const t = Math.max(0, Math.min(1, 0.5 - lead / CATCHUP_RANGE));

    return DROP_TABLE.map(([item, weight]) => {
      const bias = CATCHUP_BIAS[item];
      if (!bias) return [item, weight] as const;
      return [item, weight * (bias[0] + (bias[1] - bias[0]) * t)] as const;
    });
  }

  private explode(bomb: BombState): void {
    const s = this.state;
    const cx = Math.floor(bomb.x);
    const cy = Math.floor(bomb.y);
    const cells: number[] = [];
    const kinds: number[] = [];
    const axes: number[] = [];
    const broken: number[] = [];

    const push = (x: number, y: number, kind: number, axis: number) => {
      cells.push(idxOf(s, x, y));
      kinds.push(kind);
      axes.push(axis);
    };
    push(cx, cy, 0, 0);

    for (let d = 0; d < 4; d++) {
      const [dx, dy] = DIR_VECTORS[d];
      const axis = dx !== 0 ? 0 : 1;
      for (let r = 1; r <= bomb.power; r++) {
        const x = cx + dx * r;
        const y = cy + dy * r;
        if (!inBounds(s, x, y)) break;
        const tile = tileAt(s, x, y);
        if (tile === Tile.HARD) break;
        const isTip = r === bomb.power;
        if (tile === Tile.SOFT) {
          broken.push(idxOf(s, x, y));
          push(x, y, 2, axis);
          if (!bomb.pierce) break;
          continue;
        }
        push(x, y, isTip ? 2 : 1, axis);
        const chained = bombAt(s, x, y);
        if (chained) {
          chained.fuse = Math.min(chained.fuse, CHAIN_DELAY_TICKS);
          break;
        }
      }
    }

    // Pre-existing pickups caught in the blast are incinerated.
    for (const idx of cells) {
      if (s.items[idx] !== Item.NONE) s.items[idx] = Item.NONE;
    }
    for (const idx of broken) {
      s.grid[idx] = Tile.EMPTY;
      const x = idx % s.width;
      const y = Math.floor(idx / s.width);
      this.events.push({ t: 'break', x, y });
      const drop = this.rng.weighted(this.dropTableFor(bomb.ownerId)) as ItemValue;
      if (drop !== Item.NONE) s.items[idx] = drop;
    }

    const blast: BlastState = {
      id: s.nextBlastId++,
      ownerId: bomb.ownerId,
      cells,
      kinds,
      axes,
      ox: bomb.x,
      oy: bomb.y,
      power: bomb.power,
      ticksLeft: BLAST_TICKS,
    };
    s.blasts.push(blast);
    this.events.push({ t: 'explode', x: bomb.x, y: bomb.y, power: bomb.power, cells, kinds, axes });
    this.applyBlastDamage(blast);
  }

  private applyBlastDamage(blast: BlastState): void {
    const s = this.state;
    const hit = new Set(blast.cells);
    for (const p of s.players) {
      if (!p.alive) continue;
      const idx = idxOf(s, Math.floor(p.x), Math.floor(p.y));
      if (!hit.has(idx)) continue;
      if (s.tick < p.invulnUntil || s.tick < p.shieldUntil) continue;
      if (s.settings.mode === 'teams' && !s.settings.friendlyFire) {
        const killer = s.players.find((o) => o.id === blast.ownerId);
        if (killer && killer.id !== p.id && killer.team === p.team) continue;
      }
      /*
       * Distance along the ray, which is Manhattan because a blast is
       * axis-aligned. Scaled by the bomb's own power so the fringe of a big
       * bomb is as survivable as the fringe of a small one -- otherwise a
       * range upgrade would quietly become a damage upgrade at every distance.
       */
      const dist = Math.abs(Math.floor(p.x) - Math.floor(blast.ox)) + Math.abs(Math.floor(p.y) - Math.floor(blast.oy));
      const t = Math.min(1, dist / Math.max(1, blast.power));
      this.damagePlayer(p, blast.ownerId, 1 - (1 - BLAST_FALLOFF_MIN) * t);
    }
    for (const e of s.enemies) {
      if (!e.alive) continue;
      if (hit.has(idxOf(s, Math.floor(e.x), Math.floor(e.y)))) this.hurtEnemy(e);
    }
    // Bombs caught mid-flight or mid-kick chain too.
    for (const b of s.bombs) {
      if (b.flight > 0) continue;
      const idx = idxOf(s, Math.floor(b.x), Math.floor(b.y));
      if (hit.has(idx)) b.fuse = Math.min(b.fuse, CHAIN_DELAY_TICKS);
    }
  }

  /**
   * One hit taken, which is only sometimes a death.
   *
   * With `hitPoints` at 1 this is exactly the old behaviour and the branch
   * costs nothing. Above 1 a blast spends a hit point and grants a short
   * grace, because a blast sits on its tile for `BLAST_TICKS` and would
   * otherwise take every point in a third of a second -- one-hit death wearing
   * a health bar.
   *
   * Crushing is not routed through here on purpose: a wall closing on a player
   * in sudden death is not an exchange they can survive, and giving it a
   * health check would make the timer stop ending matches.
   */
  private damagePlayer(p: PlayerState, killerId: string | null, falloff = 1): void {
    if (this.hitsToKill <= 1) {
      this.killPlayer(p, killerId);
      return;
    }

    /*
     * One exchange, scaled at both ends.
     *
     * The attacker's resolve makes the hit land harder and the victim's makes
     * it land softer, so a player who is behind is both tougher to put down
     * and more dangerous to ignore. Both are capped low on purpose: Smash caps
     * Rage at 1.1x, and the reason is that a comeback mechanic which visibly
     * hands somebody the game is worse than not having one.
     *
     * Multiplied rather than added, so two resolved players fighting each
     * other cancel out and the mechanic stays invisible in an even match --
     * which is the same property the drop table's bias pairs have.
     */
    const attacker = killerId ? this.state.players.find((o) => o.id === killerId) : null;
    const lethal = 1 + (attacker && attacker.id !== p.id ? attacker.resolve * RESOLVE_LETHAL_MAX : 0);
    const resist = 1 - p.resolve * RESOLVE_RESIST_MAX;
    const damage = this.hitDamage * lethal * resist * falloff;

    p.hp -= damage;
    if (p.hp <= 0.5) {
      p.hp = 0;
      this.killPlayer(p, killerId);
      return;
    }
    p.invulnUntil = this.state.tick + HURT_INVULN_TICKS;
    this.events.push({ t: 'hurt', player: p.id, hp: p.hp, x: p.x, y: p.y, by: killerId });
  }

  /**
   * Recompute how far behind everyone is.
   *
   * Every tick, because that is the whole point: the mechanics that failed
   * before this one were keyed to death and fired about once per player per
   * match. This applies to every exchange, and with health in play there are
   * many exchanges.
   *
   * Standing is kills net of deaths against the field, with material at a
   * quarter weight -- the same measure `grantCharge` uses, because the
   * attribution probe found kills to be the strongest predictor of the winner
   * by some distance and material to be near noise.
   */
  private updateResolve(): void {
    const alive = this.state.players.filter((o) => o.lives > 0);
    if (!this.resolveOn || alive.length < 2) {
      for (const p of this.state.players) p.resolve = 0;
      return;
    }
    let kd = 0;
    let mat = 0;
    for (const o of alive) {
      kd += o.kills - o.deaths;
      mat += materialPower(o);
    }
    const meanKd = kd / alive.length;
    const meanMat = mat / alive.length;
    for (const p of this.state.players) {
      const deficit = meanKd - (p.kills - p.deaths) + (meanMat - materialPower(p)) * 0.25;
      p.resolve = Math.max(0, Math.min(1, deficit / RESOLVE_RANGE));
    }
  }

  private killPlayer(p: PlayerState, killerId: string | null): void {
    const s = this.state;
    p.alive = false;
    p.deaths++;
    /*
     * Deathmatch costs you the respawn timer and nothing else; only elimination
     * spends a life, and `respawn` is gated on having one left.
     *
     * Sudden death is the exception in both directions: it takes everything,
     * whatever the win condition and however many lives are left. A wall you
     * can come back from is not sudden death, and letting players respawn into
     * a closing arena is what kept the mechanic out of deathmatch entirely.
     */
    if (s.phase === 'sudden-death') p.lives = 0;
    else if (this.winBy === 'lives') p.lives = Math.max(0, p.lives - 1);
    p.respawnAt = s.tick + RESPAWN_TICKS;
    p.shieldUntil = 0;
    p.ghostUntil = 0;
    p.curse = Curse.NONE;
    p.curseUntil = 0;
    /*
     * The series rule: specials are lost, firepower and bomb count are kept.
     *
     * This is what makes a lead contestable instead of permanent. It is also
     * the closest thing this game has to League's shutdown bounty -- the
     * advantage a leader accumulated stops being theirs the moment somebody
     * takes them down, without ever punishing them for being good while they
     * are alive.
     *
     * Range and bomb count survive on purpose. They are the two the series
     * spares and the two this project measured as noise; taking them would
     * cost the leader the most visible part of their progress for no measured
     * benefit, which is the punishment this design keeps trying not to be.
     */
    if (s.settings.loseGearOnDeath) {
      p.kick = false;
      p.punch = false;
      p.remote = false;
      p.pierce = false;
      p.speedLevel = 0;
    }
    // Dropping a player's bombs would be unfair; they simply burn down.
    const killer = killerId ? s.players.find((o) => o.id === killerId) : null;
    if (killer && killer.id !== p.id) {
      killer.kills++;
      /*
       * The scoreboard stops paying full price for farming the same player.
       *
       * Floors rather than reaching zero: the kill still happened, and making
       * repeat kills worthless would only move the farming to whoever was
       * second weakest.
       */
      const decay = Math.max(FARM_SCORE_FLOOR, 1 - Math.max(0, p.deaths - 1) * FARM_SCORE_DECAY);
      killer.score += Math.round(100 * decay);
    } else {
      p.score -= 50;
    }
    this.events.push({ t: 'death', x: p.x, y: p.y, player: p.id, killer: killerId });
  }

  private stepBlasts(): void {
    const s = this.state;
    for (let i = s.blasts.length - 1; i >= 0; i--) {
      const b = s.blasts[i];
      b.ticksLeft--;
      if (b.ticksLeft > 0) {
        // Blasts stay lethal for their whole lifetime, catching players who
        // walk into a fire that is already burning.
        if (s.phase !== 'over') this.applyBlastDamage(b);
      } else {
        s.blasts.splice(i, 1);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Pickups                                                             */
  /* ------------------------------------------------------------------ */

  private stepPickups(): void {
    const s = this.state;
    for (const p of s.players) {
      if (!p.alive) continue;
      const idx = idxOf(s, Math.floor(p.x), Math.floor(p.y));
      const item = s.items[idx] as ItemValue;
      if (item === Item.NONE) continue;
      s.items[idx] = Item.NONE;
      this.applyItem(p, item);
      this.events.push({ t: 'pickup', x: p.x, y: p.y, item, player: p.id });
    }
  }

  private applyItem(p: PlayerState, item: ItemValue): void {
    const s = this.state;
    switch (item) {
      case Item.BOMB:
        p.maxBombs = Math.min(MAX_BOMBS, p.maxBombs + 1);
        break;
      case Item.FIRE:
        p.power = Math.min(MAX_POWER, p.power + 1);
        break;
      case Item.SPEED:
        p.speedLevel = Math.min(MAX_SPEED_LEVEL, p.speedLevel + 1);
        break;
      case Item.KICK:
        p.kick = true;
        break;
      case Item.PUNCH:
        p.punch = true;
        break;
      case Item.REMOTE:
        p.remote = true;
        break;
      case Item.PIERCE:
        p.pierce = true;
        break;
      case Item.SHIELD:
        p.shieldUntil = s.tick + SHIELD_TICKS;
        break;
      case Item.GHOST:
        p.ghostUntil = s.tick + GHOST_TICKS;
        break;
      case Item.FULLFIRE:
        p.power = FULL_FIRE_POWER;
        break;
      case Item.HEART:
        p.lives += 1;
        break;
      case Item.SKULL: {
        const curse = this.rng.pick(CURSE_POOL) as CurseValue;
        p.curse = curse;
        p.curseUntil = s.tick + CURSE_TICKS;
        this.events.push({ t: 'curse', player: p.id, curse });
        break;
      }
    }
    p.score += 25;
  }

  /* ------------------------------------------------------------------ */
  /* Timer, sudden death, victory                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Supply drops, on the clock and from the seed.
   *
   * Deliberately derived rather than stored: the landing ticks are a function
   * of `roundSeconds`, and the cells are a function of the match seed, so
   * nothing new goes into `PlayerState` or onto the wire. Every client that
   * simulates this match reaches the same conclusion at the same tick without
   * being told, which is what the prediction and the room both need.
   *
   * Its own `Rng`, seeded off the match seed rather than drawn from
   * `this.rng`: taking from the shared stream would shift every drop roll that
   * followed, so turning events on would change the whole economy and no
   * measurement could separate the two.
   */
  private stepSupply(): void {
    const s = this.state;
    if (s.settings.arenaEvents !== true || s.phase !== 'playing') return;
    const total = s.settings.roundSeconds * TICK_HZ;
    for (let i = 0; i < SUPPLY_AT.length; i++) {
      const at = Math.round(total * SUPPLY_AT[i]);
      if (s.timeLeft === at + SUPPLY_WARN_SECONDS * TICK_HZ) {
        this.events.push({ t: 'supply-soon' });
        return;
      }
      if (s.timeLeft === at) {
        this.dropSupply(i);
        return;
      }
    }
  }

  private dropSupply(index: number): void {
    const s = this.state;
    const rng = new Rng((s.settings.seed ^ 0x5099) + index * 7919);

    /*
     * Every tile a crate could stand on.
     *
     * A tile holding a bomb, an item, or a player is skipped, and so is any
     * tile beside a player: a block appearing in the square somebody was about
     * to escape into would kill them, and the drop is supposed to be an
     * opportunity rather than a tax.
     */
    const open: number[] = [];
    for (let y = 1; y <= s.height - 2; y++) {
      for (let x = 1; x <= s.width - 2; x++) {
        const idx = idxOf(s, x, y);
        if (s.grid[idx] !== Tile.EMPTY) continue;
        if (s.items[idx] !== Item.NONE) continue;
        if (bombAt(s, x, y)) continue;
        let nearPlayer = false;
        for (const p of s.players) {
          if (!p.alive) continue;
          if (
            Math.abs(Math.floor(p.x) - x) <= SUPPLY_CLEARANCE &&
            Math.abs(Math.floor(p.y) - y) <= SUPPLY_CLEARANCE
          ) {
            nearPlayer = true;
            break;
          }
        }
        if (!nearPlayer) open.push(idx);
      }
    }
    if (!open.length) return;

    /*
     * Spread them out, by taking the tile furthest from everything chosen so
     * far.
     *
     * The first version put the whole drop in one cluster in the middle, on
     * the theory that a contested pile is more interesting than a sprinkle.
     * Played, it is worse: whoever is nearest sweeps the lot, which is the
     * opposite of the point. Farthest-point sampling means six crates in six
     * parts of the arena and nobody close to more than one or two.
     */
    const chosen: number[] = [rng.pick(open)];
    while (chosen.length < SUPPLY_COUNT && chosen.length < open.length) {
      let best = -1;
      let bestDist = -1;
      for (const idx of open) {
        if (chosen.includes(idx)) continue;
        const x = idx % s.width;
        const y = Math.floor(idx / s.width);
        let nearest = Infinity;
        for (const c of chosen) {
          const d = Math.abs((c % s.width) - x) + Math.abs(Math.floor(c / s.width) - y);
          if (d < nearest) nearest = d;
        }
        if (nearest > bestDist) {
          bestDist = nearest;
          best = idx;
        }
      }
      if (best < 0) break;
      chosen.push(best);
    }

    /*
     * And nothing that walls anybody in.
     *
     * Clearance keeps a crate off a player and off the square beside them,
     * which stops the obvious death, but not the quieter one: six blocks in
     * the wrong six places can seal a corridor and leave somebody in a pocket
     * with nothing to do for the rest of the round. So each crate is tested
     * before it is kept -- placed, then the board is walked from every living
     * player, and if any of them can no longer reach every other player the
     * crate is dropped rather than the players being asked to bomb their way
     * out of a mechanic that was supposed to be a gift.
     *
     * Order matters and is deterministic: the crates are considered in the
     * order the sampling chose them, so every client rejects the same ones.
     */
    const keep: number[] = [];
    for (const idx of chosen) {
      s.grid[idx] = Tile.SOFT;
      if (this.playersStillConnected()) keep.push(idx);
      else s.grid[idx] = Tile.EMPTY;
    }

    /*
     * A crate, not a pickup on the floor.
     *
     * The item goes underneath and the tile becomes a soft block, so the drop
     * has to be opened rather than collected -- which costs a bomb and the
     * time to place it, and gives everyone else those seconds to arrive. The
     * item is pre-placed so a crate is never empty; when the block breaks, the
     * ordinary drop roll either leaves it alone or replaces it with another
     * item, so opening one always pays.
     *
     * It also puts soft blocks back into a board that has run out of them,
     * which is where the economy was measured to stop producing.
     */
    const cells: number[] = [];
    for (const idx of keep) {
      s.items[idx] = rng.weighted(SUPPLY_TABLE) as ItemValue;
      cells.push(idx);
    }
    if (cells.length) this.events.push({ t: 'supply', cells });
  }

  /**
   * Can every living player still walk to every other one?
   *
   * A flood fill from the first of them across open floor. Soft blocks count
   * as walls here on purpose: they can be bombed through, but "you can get out
   * eventually, with a bomb you might not have" is not the same promise as
   * "the arena is still one place".
   */
  private playersStillConnected(): boolean {
    const s = this.state;
    const alive = s.players.filter((p) => p.alive);
    if (alive.length < 2) return true;
    const start = idxOf(s, Math.floor(alive[0].x), Math.floor(alive[0].y));
    const seen = new Uint8Array(s.grid.length);
    const queue = [start];
    seen[start] = 1;
    while (queue.length) {
      const idx = queue.pop()!;
      const x = idx % s.width;
      const y = Math.floor(idx / s.width);
      for (const [dx, dy] of DIR_VECTORS) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 1 || ny < 1 || nx > s.width - 2 || ny > s.height - 2) continue;
        const ni = idxOf(s, nx, ny);
        if (seen[ni] || s.grid[ni] !== Tile.EMPTY) continue;
        seen[ni] = 1;
        queue.push(ni);
      }
    }
    return alive.every((p) => seen[idxOf(s, Math.floor(p.x), Math.floor(p.y))] === 1);
  }

  private stepTimer(): void {
    const s = this.state;
    if (s.phase === 'playing') {
      s.timeLeft--;
      this.stepSupply();
      if (s.timeLeft <= 0) {
        /*
         * A `survive` room is the one place the clock is the win, not the
         * whistle. Decided here rather than in `goalMet` because this is the
         * tick that reaches zero -- checking both places would be two paths
         * racing to end one room, and the loser would start sudden death on a
         * room that had already been won.
         */
        if (s.settings.roomGoal === RoomGoal.SURVIVE) {
          const alive = s.players.filter((p) => p.alive || p.lives > 0);
          s.winners = alive.map((p) => p.id);
          if (alive.length) this.events.push({ t: 'room-clear' });
          this.finish();
          return;
        }
        /*
         * Sudden death breaks a match the clock could not.
         *
         * It used to be elimination-only, because closing the walls in on
         * players who keep respawning just grinds them against the rubble.
         * Nobody respawns in sudden death now -- being caught by it is a wipe,
         * which is the whole point of the name -- so that objection is gone
         * and a deathmatch that ends level can be settled by play rather than
         * handed to whoever is first in the scoreboard's tiebreak.
         *
         * A deathmatch with a clear leader still ends on the whistle. There is
         * nothing to break.
         */
        if (!s.settings.suddenDeath || (this.winBy !== 'lives' && !this.undecided())) {
          this.finish();
          return;
        }
        s.phase = 'sudden-death';
        s.sdTimer = SUDDEN_DEATH_WARN_TICKS;
        /*
         * The whistle is the wipe, not just the walls.
         *
         * Nobody respawns in sudden death -- that is the rule the phase was
         * built on, and letting players come back into a closing arena is what
         * kept the mechanic out of deathmatch entirely. `respawn` enforces it
         * for anyone who dies *during* the phase, and missed the player who
         * died a second before it started: they sit in the respawn queue with
         * a life left and walk back into it when their timer comes up.
         *
         * A narrow race, and one nothing had happened to land on until arena
         * hazards shifted the timing enough that a seed did. Found by the
         * check below, which is why it is worded as a property rather than a
         * scenario.
         */
        for (const p of s.players) {
          if (!p.alive) p.lives = 0;
        }
        this.events.push({ t: 'sudden-death' });
        this.events.push({ t: 'phase', phase: 'sudden-death' });
      }
      return;
    }

    if (s.phase !== 'sudden-death') return;
    if (s.sdTimer > 0) {
      s.sdTimer--;
      return;
    }
    s.sdTimer = SUDDEN_DEATH_INTERVAL;
    // Skip border tiles that are already solid.
    while (s.sdIndex < this.spiral.length && s.grid[this.spiral[s.sdIndex]] === Tile.HARD) {
      s.sdIndex++;
    }
    if (s.sdIndex >= this.spiral.length) {
      this.finish();
      return;
    }
    const idx = this.spiral[s.sdIndex++];
    this.dropBlock(idx);
  }

  private dropBlock(idx: number): void {
    const s = this.state;
    const x = idx % s.width;
    const y = Math.floor(idx / s.width);
    s.grid[idx] = Tile.HARD;
    s.items[idx] = Item.NONE;
    for (let i = s.bombs.length - 1; i >= 0; i--) {
      const b = s.bombs[i];
      if (Math.floor(b.x) === x && Math.floor(b.y) === y) s.bombs.splice(i, 1);
    }
    for (const p of s.players) {
      if (!p.alive) continue;
      if (Math.floor(p.x) === x && Math.floor(p.y) === y) {
        /*
         * Lethal, and hit points do not change that.
         *
         * Spending one hit point instead was tried, on the reasoning that
         * health should mean surviving your first mistake everywhere. It is
         * wrong, and measurably: the tile the player is standing in has just
         * become `HARD`, the spiral skips anything already solid so it is
         * never dropped on again, and `canStandAt` samples a box around the
         * target which always overlaps the wall they are inside. So a
         * survivor cannot be crushed and cannot walk out. Across 60 matches
         * it left 162 players entombed, 24 of them with all four neighbours
         * solid, and every one of them was still in there when the match
         * ended -- one for 39.6 seconds.
         *
         * Sudden death exists to end a match. A wall that can be survived is
         * a wall that stops doing that, whatever it does for the health
         * metaphor.
         */
        p.invulnUntil = 0;
        p.shieldUntil = 0;
        this.killPlayer(p, null);
      }
    }
    /*
     * The wall takes creatures too, and for the same reason it ignores their
     * hit points: a drifter left standing inside a `HARD` tile is one that can
     * never move again and can never be blown up, so a room with `clearEnemies`
     * would become unclearable at the exact moment the game is trying to end
     * it. Entombment was already measured as the failure mode here for
     * players; a creature would just do it silently.
     */
    for (const e of s.enemies) {
      if (!e.alive) continue;
      if (Math.floor(e.x) !== x || Math.floor(e.y) !== y) continue;
      e.alive = false;
      this.events.push({ t: 'enemy-death', id: e.id, x: e.x, y: e.y, kind: e.kind });
    }
    this.events.push({ t: 'crush', x, y });
  }

  /**
   * Has the room's objective been satisfied this tick?
   *
   * `survive` is absent on purpose: nothing about it is true until the clock
   * reaches zero, and the clock lives in `stepTimer`, which reaches that
   * moment first. Deciding it here as well would be two places racing to end
   * one room.
   */
  private goalMet(alive: readonly PlayerState[]): boolean {
    const s = this.state;
    switch (s.settings.roomGoal) {
      case RoomGoal.CLEAR:
        return s.enemies.length > 0 && s.enemies.every((e) => !e.alive);
      case RoomGoal.HUNT: {
        const quarry = s.enemies.filter((e) => e.hunted);
        return quarry.length > 0 && quarry.every((e) => !e.alive);
      }
      case RoomGoal.EXIT:
        return (
          s.exitAt >= 0 &&
          alive.some((p) => p.alive && idxOf(s, Math.floor(p.x), Math.floor(p.y)) === s.exitAt)
        );
      default:
        return false;
    }
  }

  private checkVictory(): void {
    const s = this.state;
    if (s.phase === 'over') return;

    /*
     * A creature room is decided by its objective.
     *
     * Before the player check below, because an Expedition room holds exactly
     * one player and that check exists to stop a solo arena declaring a winner
     * on tick one. Last-one-standing cannot decide a room with nobody to be
     * last against, so the room's own goal decides it instead.
     */
    if (s.settings.roomGoal) {
      const alive = s.players.filter((p) => p.alive || p.lives > 0);
      // One loss condition for every objective: the room is over when you are.
      if (!alive.length) {
        s.winners = [];
        this.finish();
        return;
      }
      if (this.goalMet(alive)) {
        s.winners = alive.map((p) => p.id);
        this.events.push({ t: 'room-clear' });
        this.finish();
      }
      return;
    }

    // A solo arena (training, or a lobby that emptied) has nobody to beat, so
    // last-one-standing must not fire the moment the match begins.
    if (s.players.length < 2) return;

    // Deathmatch: nobody is ever eliminated, so the only early finish is
    // somebody reaching the target. `timed` has no target and runs the clock.
    // Once the walls are closing, though, every mode is last-one-standing --
    // that is what the phase is for and how a drawn deathmatch gets settled.
    if (this.winBy !== 'lives' && s.phase !== 'sudden-death') {
      if (this.winBy !== 'kills') return;
      const target = Math.max(1, s.settings.killTarget);
      if (s.settings.mode === 'teams') {
        const byTeam = this.killsByTeam();
        for (const [team, kills] of byTeam) {
          if (kills < target) continue;
          s.winners = s.players.filter((p) => p.team === team).map((p) => p.id);
          this.finish();
          return;
        }
        return;
      }
      const front = s.players.filter((p) => p.kills >= target);
      if (front.length) {
        const best = Math.max(...front.map((p) => p.kills));
        s.winners = front.filter((p) => p.kills === best).map((p) => p.id);
        this.finish();
      }
      return;
    }

    const contenders = s.players.filter((p) => p.alive || p.lives > 0);

    // A match with people in it is over when the people are out. Sitting
    // through four bots finishing the round you were knocked out of in the
    // first thirty seconds is not a game, and it is what every campaign stage
    // did on a loss.
    const humans = s.players.filter((p) => !p.bot);
    if (humans.length && !humans.some((p) => p.alive || p.lives > 0)) {
      s.winners = contenders.map((p) => p.id);
      this.finish();
      return;
    }

    if (s.settings.mode === 'teams') {
      const teams = new Set(contenders.map((p) => p.team));
      if (teams.size <= 1) {
        s.winners = contenders.map((p) => p.id);
        this.finish();
      }
      return;
    }

    if (s.settings.mode === 'survival') {
      const humans = contenders.filter((p) => !p.bot);
      if (humans.length === 0) {
        s.winners = [];
        this.finish();
      }
      return;
    }

    if (contenders.length <= 1) {
      s.winners = contenders.map((p) => p.id);
      this.finish();
    }
  }

  /**
   * The win condition actually in force.
   *
   * Survival is a wave mode built on being eliminated — it ends when the last
   * human is out — so it ignores the setting. Nothing else stops a host from
   * pairing it with a kill target and making the match unwinnable.
   */
  /**
   * Whether the drop table bends toward whoever is behind.
   *
   * Settings rather than a constant, so a competitive ruleset can pin it off
   * and reproduce the flat table exactly. Defaults on: a match that does not
   * say is a match somebody is trying to enjoy.
   */
  /** Clean hits a player survives. 1 is the original one-hit game. */
  private get hitsToKill(): number {
    return Math.max(1, Math.round(this.state.settings.hitPoints ?? 1));
  }

  /** What one unmodified blast costs, as a share of full health. */
  private get hitDamage(): number {
    return MAX_HEALTH / this.hitsToKill;
  }

  /** Resolve needs health to scale; in a one-hit game there is nothing to scale. */
  private get resolveOn(): boolean {
    return this.state.settings.resolve === true && this.hitsToKill > 1;
  }

  private get catchup(): boolean {
    return this.state.settings.catchup !== false;
  }

  private get winBy(): WinCondition {
    return this.state.settings.mode === 'survival' ? 'lives' : this.state.settings.winBy;
  }

  /** Combined kills per team, for the team variants of the deathmatch modes. */
  /**
   * Would the whistle leave this match a draw?
   *
   * The same order `finish` grades a deathmatch by -- most kills, score
   * breaking the tie -- asked one step earlier, so the clock can hand an
   * unresolved match to sudden death instead of resolving it by tiebreak.
   */
  private undecided(): boolean {
    const s = this.state;
    if (s.players.length < 2) return false;
    if (s.settings.mode === 'teams') {
      const byTeam = [...this.killsByTeam().values()];
      const best = Math.max(...byTeam);
      return byTeam.filter((k) => k === best).length > 1;
    }
    const best = Math.max(...s.players.map((p) => p.kills));
    const front = s.players.filter((p) => p.kills === best);
    if (front.length < 2) return false;
    const top = Math.max(...front.map((p) => p.score));
    return front.filter((p) => p.score === top).length > 1;
  }

  private killsByTeam(): Map<number, number> {
    const out = new Map<number, number>();
    for (const p of this.state.players) out.set(p.team, (out.get(p.team) ?? 0) + p.kills);
    return out;
  }

  private finish(): void {
    const s = this.state;
    if (s.phase === 'over') return;
    if (!s.winners.length && this.winBy !== 'lives' && s.players.length > 1) {
      // Deathmatch ran out of clock: most kills takes it, score breaks a tie.
      if (s.settings.mode === 'teams') {
        const byTeam = this.killsByTeam();
        const best = Math.max(0, ...byTeam.values());
        const winning = [...byTeam].filter(([, k]) => k === best).map(([team]) => team);
        s.winners = s.players.filter((p) => winning.includes(p.team)).map((p) => p.id);
      } else {
        const best = Math.max(...s.players.map((p) => p.kills));
        const front = s.players.filter((p) => p.kills === best);
        const topScore = Math.max(...front.map((p) => p.score));
        s.winners = front.filter((p) => p.score === topScore).map((p) => p.id);
      }
    }
    if (!s.winners.length) {
      // Timeout with nobody eliminated: highest score takes it.
      const alive = s.players.filter((p) => p.alive || p.lives > 0);
      if (alive.length) {
        const best = Math.max(...alive.map((p) => p.score));
        s.winners = alive.filter((p) => p.score === best).map((p) => p.id);
      }
    }
    for (const id of s.winners) {
      const p = s.players.find((o) => o.id === id);
      if (p) p.score += 500;
    }
    s.phase = 'over';
    this.events.push({ t: 'phase', phase: 'over' });
  }

  /* ------------------------------------------------------------------ */
  /* Survival mode helpers                                               */
  /* ------------------------------------------------------------------ */

  /** Injects a fresh wave of bots. Used by the survival game mode. */
  spawnWave(seeds: PlayerSeed[]): void {
    const s = this.state;
    for (const seed of seeds) {
      const p = createPlayer(seed, 1, MAX_HEALTH);
      const spawn = this.spawns[seed.slot % this.spawns.length];
      p.x = spawn[0] + 0.5;
      p.y = spawn[1] + 0.5;
      p.invulnUntil = s.tick + SPAWN_INVULN_TICKS;
      s.players.push(p);
    }
    s.wave++;
    this.events.push({ t: 'wave', wave: s.wave });
  }
}
